import { FormAlert } from '@frontend/jetlend-web-ui/src/ui/forms/FormAlert/FormAlert';
import PropertyItem from '@frontend/jetlend-web-ui/src/components/PropertyItem/PropertyItem';
import {
    CURRENCY_RUB,
    formatAmountShort,
    formatPercentExt,
} from '@frontend/jetlend-core/src/formatters/formatUtils';
import Button from '@frontend/jetlend-web-ui/src/ui/inputs/Button/Button';
import MaskWrapper, { IProps as MaskWrapperProps } from '@frontend/jetlend-web-ui/src/components/MaskWrapper/MaskWrapper';
import useStoreHandler from '@frontend/jetlend-core/src/hooks/useStoreHandler';
import styles from './ScoringSuccess.module.scss';
import { BorrowerLimitPrescoringStoreData } from '@app/models/borrowers/limitPrescoring';
import { limitPrescoringStoreHandler } from '@app/ducks/borrowers/borrowers';

export interface Props extends Pick<MaskWrapperProps, 'masked'> {
    /**
     * Текст заголовка
     */
    title?: string;
    /**
     * Флаг сокрытия кнопки продолжить
     */
    hideContinueButton?: boolean;
}
/**
 * В случае отказа по скорингу до ввода контактов отображается этот компонент
 * В этом случае, чтобы длина строки за маской была правдоподобной,
 * Используются эти плейсхолдеры
 */
const DEFAULT_TERM = 'до 00 мес.';
const DEFAULT_LIMIT = `00 000 000 ${CURRENCY_RUB}`;
const DEFAULT_RATE = '00 %';

/**
 * Компонент для отображения успешного результата скоринга
 */
export default function ScoringSuccess({
    masked,
    title,
    hideContinueButton,
}: Props) {
    const [ data ] = useStoreHandler<BorrowerLimitPrescoringStoreData>(limitPrescoringStoreHandler);

    return (
        <div className={styles['content']}>
            {title && (
                <h2 className={styles['title']} data-testid="limit-prescoring-modal__title">
                    {title}
                </h2>
            )}

            <div className={styles['property-items-container']}>
                <PropertyItem
                    title="Максимальный лимит"
                    value={(
                        <span className={styles['text-amount']}>
                            <MaskWrapper masked={masked}>
                                {
                                    /**
                                     * Дефолтное значение устанавливается для корректной ширины контента за маской
                                     */
                                    formatAmountShort(data.limit_amount, CURRENCY_RUB, true, DEFAULT_LIMIT)
                                }
                            </MaskWrapper>
                        </span>
                    )}
                    align="left"
                    className={styles['property-item']}
                    valueClassName={styles['property-item--value']}
                    titleClassName={styles['property-item--title']}
                />

                <PropertyItem
                    title="Срок возврата транша"
                    value={(
                        <MaskWrapper masked={masked}>
                            {
                                /**
                                 * Дефолтное значение устанавливается для корректной ширины контента за маской
                                 */
                                data?.term_months ? `до ${data.term_months} мес.` : DEFAULT_TERM
                            }
                        </MaskWrapper>
                    )}
                    align="left"
                    className={styles['property-item']}
                    valueClassName={styles['property-item--value']}
                    titleClassName={styles['property-item--title']}
                />

                <PropertyItem
                    title="Ставка"
                    value={(
                        <MaskWrapper masked={masked}>
                            {
                                /**
                                 * Дефолтное значение устанавливается для корректной ширины контента за маской
                                 */
                                formatPercentExt(data.rate, { defaultValue: DEFAULT_RATE })
                            }
                        </MaskWrapper>
                    )}
                    align="left"
                    className={styles['property-item']}
                    valueClassName={styles['property-item--value']}
                    titleClassName={styles['property-item--title']}
                />
            </div>

            <FormAlert
                alert="Открытие ВЛЗ не приводит к выборке транша, не передает никакие данные в БКИ, а также ни к чему не обязывает"
                type="success"
                className={styles['alert']}
            />

            {!hideContinueButton && (
                <Button
                    containerClassName={styles['button-container']}
                    className={styles['button']}
                    size="big"
                    href={data.redirect_url}
                >
                    Продолжить
                </Button>
            )}
        </div>
    );
}